import type { FC } from 'react';
import IconMdi from '@components/IconMdi';
import { mdiAlert } from '@mdi/js';

import type { DocType, ErrorPageProps, ErrorType } from '../types';

const getErrorPageContent = (type: ErrorType, docType?: DocType) =>
  ({
    unknown: {
      title: 'unknown error',
      description: 'The platform has encountered an error.',
      helperText: 'Please try to refresh the page, or return to home.',
    },
    document: {
      title: `${docType} missing`,
      description: `Unable to load ${docType}.`,
      helperText: 'Please, check the URL and try again, or return to home.',
    },
    500: {
      title: '500 error',
      description: 'Internal Server Error.',
      helperText: 'The server has encountered an unexpected condition and is unable to fulfil the request.',
    },
    404: {
      title: '404 error',
      description: 'Page not found.',
      helperText: 'The page you were looking for does not exist.',
    },
  })[type];

export const ProErrorPage: FC<ErrorPageProps> = ({ type, docType, onNavigateHome, onRefresh }) => {
  const content = getErrorPageContent(type, docType);

  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="inline-flex h-1/2 items-center justify-center bg-white">
        <div className="inline-flex shrink grow basis-0 flex-col items-center justify-start gap-6 self-stretch">
          <div className="bg-error-100 inline-flex items-center justify-center gap-2.5 rounded-[90px] p-2">
            <IconMdi path={mdiAlert} className="text-error-400 relative h-8 w-8" />
          </div>
          <div className="flex h-[108px] flex-col items-center justify-start gap-2 self-stretch">
            <div className="text-error-600 self-stretch text-center text-sm leading-tight font-semibold tracking-tight uppercase">
              {content.title}
            </div>
            <div className="text-info-900 self-stretch text-center text-[32px] leading-[48px] font-extrabold">
              {content.description}
            </div>
            <div className="text-info-500 self-stretch text-center text-base leading-normal font-normal">
              {content.helperText}
            </div>
          </div>
          <div className="inline-flex items-start justify-center gap-3">
            {type !== '404' && (
              <button
                className="bg-error-600 flex items-center justify-center rounded-md px-[17px] py-[9px] shadow-sm"
                onClick={onRefresh}
              >
                <div className="text-sm leading-tight font-medium text-white">Refresh page</div>
              </button>
            )}
            <button
              className="bg-error-100 flex items-center justify-center rounded-md px-[17px] py-[9px] shadow-sm"
              onClick={onNavigateHome}
            >
              <div className="text-error-700 text-sm leading-tight font-medium">Go back home</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
